import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeft, Star, Trophy, MessageSquarePlus, Download, Languages, Pencil, Save, X, Bell, Globe, Sparkles, Copy, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";
import StatusBadge from "../StatusBadge";
import { STATUSES, PRIORITIES, PRIORITY_STYLE, AUDIENCES, APP_TYPES, CATEGORIES, ADVANCEMENT, COUNTRIES, langName } from "../../lib/constants";
import { useLang } from "../../context/LanguageContext";
import ProjectDossier from "../dossier/ProjectDossier";
import { statusSlug, statusLabel, priorityLabel } from "../../lib/statusI18n";

const CRITERIA = ["Originalité", "Utilité", "Faisabilité", "Potentiel utilisateurs", "Potentiel international", "Innovation", "Modèle économique potentiel", "Qualité de présentation"];
const FAVS = ["À revoir", "Intéressant", "Très intéressant", "Prioritaire"];

// Espace de travail complet d'un dossier : l'admin consulte, modifie, enregistre,
// traduit et répond SANS jamais quitter la section « Projets par utilisateur »
// (ce composant est aussi utilisé par la fiche classique de la liste Demandes).
export default function ProjectWorkspace({ pid, onBack, backLabel }) {
  const { tf: t } = useLang();
  const [p, setP] = useState(null);
  const [ratings, setRatings] = useState({});
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [editCand, setEditCand] = useState(false);
  const [candForm, setCandForm] = useState({});
  const [savingCand, setSavingCand] = useState(false);
  const dossierRef = useRef(null);
  const markedRead = useRef(false);

  const load = useCallback(() => api.get(`/projects/${pid}`).then((r) => {
    setP(r.data);
    setRatings(r.data.ratings || {});
    // Ouverture du dossier → pastille « nouveaux messages » effacée côté liste.
    if (!markedRead.current && (r.data.unread_messages || 0) > 0) {
      markedRead.current = true;
      api.post(`/admin/projects/${pid}/messages/read`).catch(() => {});
    }
  }).catch(() => onBack && onBack()), [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!p) return <div className="py-20 text-center text-ink-secondary">{t("adm_loading")}</div>;

  const critLabel = (c) => { const k = `crit_${statusSlug(c)}`; const v = t(k); return v === k ? c : v; };
  const favLabel = (f) => { const k = `fav_${statusSlug(f)}`; const v = t(k); return v === k ? f : v; };
  const total = CRITERIA.reduce((a, c) => a + (Number(ratings[c]) || 0), 0);
  const clientLang = (p.lang || "fr").toLowerCase();
  const isForeign = clientLang !== "fr";
  const cand = p.candidate || null;

  const setStatus = async (status) => {
    try { await api.put(`/admin/projects/${pid}/status`, { status }); toast.success(`${t("apd_status_toast")}${statusLabel(t, status)}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveRatings = async () => {
    try { await api.put(`/admin/projects/${pid}/ratings`, { ratings }); toast.success(t("apd_rating_saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  // Pré-remplissage des curseurs d'après les 8 notes de l'analyse IA (0-10 par critère).
  const autoFillRatings = () => {
    const n = analysis?.data?.notes || {};
    const next = {};
    CRITERIA.forEach((c) => { next[c] = Math.max(0, Math.min(10, Math.round(Number(n[c]) || 0))); });
    setRatings(next);
    toast.success("Curseurs pré-remplis d'après l'analyse — vérifiez, ajustez, puis « Enregistrer la notation ».");
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/projects/${pid}/note`, { text: note }); setNote(""); toast.success(t("apd_note_added")); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setFav = async (fav) => {
    try { await api.put(`/admin/projects/${pid}/favorite`, { favorite: p.favorite === fav ? null : fav }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setPriority = async (priority) => {
    try { await api.put(`/admin/projects/${pid}/priority`, { priority: p.priority === priority ? null : priority }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const selectWinner = async () => {
    if (!window.confirm(t("apd_confirm_winner"))) return;
    try { await api.post(`/admin/projects/${pid}/select-winner`); toast.success(t("apd_winner_ok")); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // ---------- Édition complète du contenu ----------
  const startEdit = () => {
    setForm({
      app_name: p.app_name || "",
      title: p.title_fr || p.title || "",
      description: p.description_fr || p.description || "",
      problem: p.problem_fr || p.problem || "",
      audience: p.audience || "", app_type: p.app_type || "", category: p.category || "",
      advancement: p.advancement || "", slogan: p.slogan || "",
      similar_apps_exist: p.similar_apps_exist || "",
      differentiation: p.differentiation || "", origin: p.origin || "", advantage: p.advantage || "",
    });
    setEditing(true);
  };
  const saveContent = async () => {
    setSaving(true);
    try { await api.put(`/admin/projects/${pid}/content`, form); toast.success("Contenu enregistré."); setEditing(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // ---------- Édition fiche candidat ----------
  const startCandEdit = () => {
    setCandForm({ first_name: cand?.first_name || "", last_name: cand?.last_name || "", email: cand?.email || "", phone: cand?.phone || "", country: cand?.country || "" });
    setEditCand(true);
  };
  const saveCandidate = async () => {
    setSavingCand(true);
    try { await api.put(`/admin/projects/${pid}/candidate`, candForm); toast.success("Fiche candidat enregistrée."); setEditCand(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSavingCand(false); }
  };

  // ---------- Bouton « Traduire en français » (traduction en direct) ----------
  const translateNow = async () => {
    setTranslating(true);
    try {
      const r = await api.post(`/admin/projects/${pid}/translate`);
      if ((r.data.lang || "fr") === "fr") toast.info("Cette demande est déjà rédigée en français — aucune traduction nécessaire.");
      else toast.success(`Demande traduite en français (langue détectée : ${langName(r.data.lang)}).`);
      load();
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setTranslating(false); }
  };

  // ---------- Bouton « Analyser le projet » (critères de sélection ACA) ----------
  const analyzeNow = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/admin/projects/${pid}/analyze`);
      toast.success("Analyse du projet terminée.");
      load();
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setAnalyzing(false); }
  };
  const analysis = p.ai_analysis || null;
  // Copie du compte rendu complet (texte brut) — à coller dans un message au porteur.
  const copyAnalysis = async () => {
    const d = analysis?.data;
    if (!d) return;
    const txt = [
      "ANALYSE DU PROJET — APP CHALLENGE ACADEMY",
      `Projet : ${p.app_name || p.title_fr || p.title} (${p.ref})`,
      `Score : ${d.score ?? "—"}/100 — Recommandation : ${RECO[d.recommandation]?.label || d.recommandation || "—"}`,
      "",
      "CRITÈRES DE SÉLECTION :",
      ...(d.criteres || []).map((c, i) => `${i + 1}. ${CRITERIA_LABELS[c.cle] || c.cle} : ${(c.verdict || "").toUpperCase()} — ${c.commentaire}`),
      "",
      d.points_forts?.length ? `POINTS FORTS :\n${d.points_forts.map((x) => `- ${x}`).join("\n")}` : null,
      d.points_faibles?.length ? `\nPOINTS DE VIGILANCE :\n${d.points_faibles.map((x) => `- ${x}`).join("\n")}` : null,
      d.argumentaire ? `\nARGUMENTAIRE :\n${d.argumentaire}` : null,
    ].filter((x) => x !== null).join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Compte rendu copié — collez-le dans votre message au porteur.");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      toast.success("Compte rendu copié — collez-le dans votre message au porteur.");
    }
  };
  const CRITERIA_LABELS = {
    gain_temps: "Fait gagner du temps à l'utilisateur",
    gain_temps_chacun: "Fait gagner du temps à CHAQUE utilisateur",
    perte_temps: "Risque de faire perdre du temps",
    auto_alimentation: "S'auto-alimente par l'utilisateur",
    ralentissement_utile: "Ralentit utilement un processus trop rapide",
    geolocalisation: "Géolocalisation pertinente et respectueuse",
    sociabilite: "Sociabilité du produit (crée du lien)",
    viralite: "Effet viral de l'idée (bouche-à-oreille)",
    valeurs: "Respecte les valeurs ACA",
  };
  const verdictStyle = (cle, v) => {
    // Pour « perte de temps », un verdict « oui » est un signal NÉGATIF (rouge).
    const bad = (cle === "perte_temps") ? (v === "oui") : (v === "non");
    const mid = v === "partiel";
    return bad ? "border-red-400/40 bg-red-500/10 text-red-300"
      : mid ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
      : "border-green-400/40 bg-green-500/10 text-green-300";
  };
  const RECO = {
    selectionner: { label: "À sélectionner", cls: "border-cyan bg-cyan/15 text-cyan" },
    approfondir: { label: "À approfondir", cls: "border-amber-400/50 bg-amber-500/10 text-amber-300" },
    refuser: { label: "À refuser", cls: "border-red-400/50 bg-red-500/10 text-red-300" },
  };

  const F = ({ label, k, textarea }) => (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</div>
      {textarea
        ? <textarea value={form[k]} rows={4} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`content-field-${k}`}
            className="w-full resize-y rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-white outline-none focus:border-cyan" />
        : <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`content-field-${k}`}
            className="w-full border border-white/10 bg-surface px-4 py-2.5 text-sm text-white outline-none focus:border-cyan" />}
    </div>
  );
  const Sel = ({ label, k, options }) => (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</div>
      <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`content-field-${k}`}
        className="w-full border border-white/10 bg-void px-3 py-2.5 text-sm text-white outline-none focus:border-cyan">
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <button onClick={onBack} data-testid="workspace-back-button" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary transition-colors hover:text-white">
        <ArrowLeft size={15} /> {backLabel}
      </button>

      {/* Bannière nouveaux messages du client */}
      {(p.unread_messages || 0) > 0 && (
        <button onClick={() => dossierRef.current?.scrollIntoView({ behavior: "smooth" })} data-testid="workspace-unread-banner"
          className="mt-4 flex w-full items-center gap-3 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-left text-sm text-red-300 transition-colors hover:bg-red-500/15">
          <Bell size={16} className="shrink-0 animate-pulse" />
          <span><b>{p.unread_messages}</b> nouveau{p.unread_messages > 1 ? "x" : ""} message{p.unread_messages > 1 ? "s" : ""} du client à lire — cliquez pour aller aux échanges.</span>
        </button>
      )}

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-8">
        <div className="flex items-center gap-4">
          {p.logo ? <img src={p.logo} alt="" className="h-16 w-16 border border-white/10 object-contain p-1" /> : <div className="flex h-16 w-16 items-center justify-center bg-elevated font-display text-2xl text-cyan">{(p.app_name || p.title || "?")[0]}</div>}
          <div>
            <h1 className="font-display text-3xl font-900 text-white">{p.app_name || p.title_fr || p.title}</h1>
            <div className="mt-1 text-sm text-ink-tertiary">{p.ref}</div>
            {/* Langue du client — TOUJOURS affichée en titre */}
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-surface px-3 py-1 text-[11px] font-600 text-white" data-testid="client-lang-strip">
              <Globe size={12} className="text-cyan" />
              {cand ? `${cand.first_name} ${cand.last_name}`.trim() || "Client" : "Client"}
              {cand?.country ? ` · ${cand.country}` : ""}
              {" · Langue : "}
              <span className="text-cyan">{langName(clientLang)}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {isForeign && (
                <div className="inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[10px] font-600 uppercase tracking-wider text-cyan" data-testid="apd-translated-badge">
                  <Languages size={11} /> Demande rédigée en {langName(clientLang)} — affichée en français
                </div>
              )}
              {/* Bouton TOUJOURS visible : la détection automatique peut se tromper
                  (ex. espagnol pris pour du français) — un clic traduit tout en français. */}
              <button onClick={translateNow} disabled={translating} data-testid="workspace-translate-btn"
                title="Traduire tout le contenu de la demande en français"
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan bg-cyan/15 px-3 py-1 text-[10px] font-700 uppercase tracking-wider text-cyan transition-all hover:bg-cyan hover:text-void disabled:opacity-50">
                <Languages size={11} /> {translating ? "Traduction en cours…" : "Traduire en français"}
              </button>
              {/* Analyse IA selon les critères de sélection ACA — bien en évidence sur chaque fiche */}
              <button onClick={analyzeNow} disabled={analyzing} data-testid="workspace-analyze-btn"
                title="Analyser ce projet selon les critères de sélection ACA (temps, auto-alimentation, valeurs) avec argumentaire d'exemple"
                className="inline-flex items-center gap-1.5 rounded-full bg-cyan px-4 py-1.5 text-[10px] font-700 uppercase tracking-wider text-void transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-6px_rgba(0,229,255,0.5)] disabled:opacity-50">
                <Sparkles size={12} /> {analyzing ? "Analyse en cours…" : "Analyser le projet"}
              </button>
            </div>
          </div>
        </div>
        <StatusBadge status={p.status} />
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
        {/* LEFT: content */}
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Contenu de la demande</div>
            {!editing ? (
              <button onClick={startEdit} data-testid="workspace-edit-content" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-600 text-ink-secondary transition-colors hover:border-cyan hover:text-cyan">
                <Pencil size={12} /> Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} data-testid="workspace-cancel-content" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-600 text-ink-secondary hover:text-white"><X size={12} /> Annuler</button>
                <button onClick={saveContent} disabled={saving} data-testid="workspace-save-content" className="inline-flex items-center gap-1.5 rounded-full bg-cyan px-4 py-1.5 text-xs font-700 text-void transition-all hover:-translate-y-0.5 disabled:opacity-50"><Save size={12} /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
              </div>
            )}
          </div>

          {!editing ? (
            <>
              <Block title={t("apd_idea")} body={p.title_fr || p.title} orig={p.title_fr ? p.title : null} />
              <Block title={t("apd_description")} body={p.description_fr || p.description} orig={p.description_fr ? p.description : null} />
              <Block title={t("apd_problem")} body={p.problem_fr || p.problem} orig={p.problem_fr ? p.problem : null} />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Meta l={t("apd_meta_public")} v={p.audience} /><Meta l={t("apd_meta_type")} v={p.app_type} /><Meta l={t("apd_meta_category")} v={p.category} />
                <Meta l={t("apd_meta_advancement")} v={p.advancement} /><Meta l={t("apd_meta_slogan")} v={p.slogan} /><Meta l={t("apd_meta_similar")} v={p.similar_apps_exist} />
              </div>
              {p.differentiation && <Block title={t("apd_differentiation")} body={p.differentiation} />}
              {p.origin && <Block title={t("apd_origin")} body={p.origin} />}
              {p.advantage && <Block title={t("apd_advantage")} body={p.advantage} />}
            </>
          ) : (
            <div className="space-y-4 rounded-xl border border-cyan/25 bg-cyan/[0.03] p-5" data-testid="workspace-edit-form">
              <F label="Nom de l'application" k="app_name" />
              <F label={t("apd_idea")} k="title" />
              <F label={t("apd_description")} k="description" textarea />
              <F label={t("apd_problem")} k="problem" textarea />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Sel label={t("apd_meta_public")} k="audience" options={AUDIENCES} />
                <Sel label={t("apd_meta_type")} k="app_type" options={APP_TYPES} />
                <Sel label={t("apd_meta_category")} k="category" options={CATEGORIES} />
                <Sel label={t("apd_meta_advancement")} k="advancement" options={ADVANCEMENT} />
                <F label={t("apd_meta_slogan")} k="slogan" />
                <F label={t("apd_meta_similar")} k="similar_apps_exist" />
              </div>
              <F label={t("apd_differentiation")} k="differentiation" textarea />
              <F label={t("apd_origin")} k="origin" textarea />
              <F label={t("apd_advantage")} k="advantage" textarea />
            </div>
          )}

          {p.documents?.length > 0 && (
            <div>
              <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_documents")} ({p.documents.length})</div>
              <div className="space-y-2">
                {p.documents.map((d, i) => (
                  <a key={i} href={d.data} download={d.name} className="flex items-center justify-between border border-white/10 bg-surface px-4 py-2.5 text-sm text-white transition-colors hover:border-cyan" data-testid={`admin-doc-${i}`}>
                    {d.name} <Download size={15} className="text-cyan" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div>
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_internal_notes")}</div>
            <div className="space-y-2">
              {(p.admin_notes || []).map((n, i) => (
                <div key={i} className="border border-white/10 bg-surface p-3 text-sm">
                  <div className="text-white">{n.text}</div>
                  <div className="mt-1 text-[11px] text-ink-tertiary">{n.admin} · {new Date(n.at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("apd_add_note")} data-testid="admin-note-input"
                className="flex-1 border border-white/10 bg-surface px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan" />
              <button onClick={addNote} data-testid="admin-note-add" className="inline-flex items-center gap-1.5 bg-elevated px-4 text-sm text-cyan transition-colors hover:bg-cyan hover:text-void"><MessageSquarePlus size={16} /></button>
            </div>
          </div>
        </div>

        {/* RIGHT: actions */}
        <div className="space-y-8">
          {/* Analyse IA du projet — critères de sélection ACA */}
          <div className="border border-cyan/25 bg-surface p-5" data-testid="analysis-panel">
            <div className="mb-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-cyan"><Sparkles size={12} /> Analyse du projet</span>
              {analysis && (
                <button onClick={analyzeNow} disabled={analyzing} data-testid="analysis-rerun"
                  className="text-[11px] font-600 text-ink-secondary transition-colors hover:text-cyan">
                  {analyzing ? "Analyse…" : "Relancer"}
                </button>
              )}
            </div>
            {!analysis ? (
              <div className="text-center">
                <p className="mb-4 text-xs leading-relaxed text-ink-secondary">Évaluez ce dossier selon les critères de sélection ACA : gain/perte de temps pour l'utilisateur, auto-alimentation par l'utilisateur, valeurs — avec argumentaire d'exemple.</p>
                <button onClick={analyzeNow} disabled={analyzing} data-testid="analysis-run"
                  className="inline-flex w-full items-center justify-center gap-2 bg-cyan px-4 py-3 text-sm font-700 uppercase tracking-wide text-void transition-all hover:-translate-y-0.5 disabled:opacity-50">
                  <Sparkles size={16} /> {analyzing ? "Analyse en cours…" : "Analyser le projet"}
                </button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="analysis-result">
                <div className="flex items-center justify-between">
                  <span className={`font-display text-4xl font-900 ${Number(analysis.data?.score) >= 70 ? "text-cyan" : Number(analysis.data?.score) >= 40 ? "text-amber-400" : "text-red-400"}`} data-testid="analysis-score">
                    {analysis.data?.score ?? "—"}<span className="text-sm text-ink-tertiary">/100</span>
                  </span>
                  {RECO[analysis.data?.recommandation] && (
                    <span className={`rounded-full border px-3 py-1.5 text-[11px] font-700 uppercase tracking-wider ${RECO[analysis.data.recommandation].cls}`} data-testid="analysis-reco">
                      {RECO[analysis.data.recommandation].label}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {(analysis.data?.criteres || []).map((c, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-void/40 p-3" data-testid={`analysis-critere-${c.cle}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-600 text-white">{CRITERIA_LABELS[c.cle] || c.cle}</span>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-700 uppercase ${verdictStyle(c.cle, c.verdict)}`}>{c.verdict}</span>
                      </div>
                      <p className="mt-1 text-[11px] leading-relaxed text-ink-secondary">{c.commentaire}</p>
                    </div>
                  ))}
                </div>
                {analysis.data?.points_forts?.length > 0 && (
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-green-400">Points forts</div>
                    <ul className="list-inside list-disc space-y-0.5 text-[11px] text-ink-secondary">
                      {analysis.data.points_forts.map((pt, i) => <li key={i}>{pt}</li>)}
                    </ul>
                  </div>
                )}
                {analysis.data?.points_faibles?.length > 0 && (
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-red-400">Points de vigilance</div>
                    <ul className="list-inside list-disc space-y-0.5 text-[11px] text-ink-secondary">
                      {analysis.data.points_faibles.map((pt, i) => <li key={i}>{pt}</li>)}
                    </ul>
                  </div>
                )}
                {analysis.data?.argumentaire && (
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-cyan">Argumentaire d'exemple</div>
                    <p className="whitespace-pre-line rounded-lg border border-cyan/25 bg-cyan/[0.05] p-3 text-[11px] leading-relaxed text-ink-secondary" data-testid="analysis-argumentaire">{analysis.data.argumentaire}</p>
                  </div>
                )}
                <div className="text-[10px] italic text-ink-tertiary">Analysé le {new Date(analysis.at).toLocaleString("fr-FR")} par {analysis.by}</div>
                <button onClick={copyAnalysis} data-testid="analysis-copy"
                  title="Copier tout le compte rendu (score, critères, points forts, argumentaire) pour l'envoyer au porteur"
                  className="inline-flex w-full items-center justify-center gap-2 border border-cyan/40 bg-cyan/10 px-4 py-2.5 text-xs font-700 uppercase tracking-wider text-cyan transition-colors hover:bg-cyan hover:text-void">
                  <Copy size={13} /> Copier le compte rendu complet
                </button>
              </div>
            )}
          </div>

          {/* Fiche candidat — consultable et modifiable sans quitter l'espace projet */}
          <div className="border border-white/10 bg-surface p-5" data-testid="candidate-card">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_candidate")}</span>
              {cand && !editCand && (
                <button onClick={startCandEdit} data-testid="candidate-edit" className="inline-flex items-center gap-1 text-[11px] font-600 text-ink-secondary transition-colors hover:text-cyan"><Pencil size={11} /> Modifier</button>
              )}
            </div>
            {!cand ? (
              <div className="text-sm text-ink-secondary">{p.author_email || "Compte supprimé"}</div>
            ) : !editCand ? (
              <div className="flex items-center gap-3">
                {cand.avatar
                  ? <img src={cand.avatar} alt="" className="h-11 w-11 shrink-0 rounded-full border border-white/10 object-cover" />
                  : <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-sm font-700 text-cyan">{`${(cand.first_name || "?")[0]}${(cand.last_name || "")[0] || ""}`.toUpperCase()}</span>}
                <div className="min-w-0 text-sm">
                  <div className="font-600 text-white">{`${cand.first_name} ${cand.last_name}`.trim() || "—"}</div>
                  <div className="truncate text-xs text-ink-tertiary">{cand.email}</div>
                  <div className="mt-0.5 text-[11px] text-ink-tertiary">
                    {[cand.phone, cand.country].filter(Boolean).join(" · ") || "—"}
                    {` · Langue : ${langName(clientLang)}`}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5" data-testid="candidate-edit-form">
                <div className="grid grid-cols-2 gap-2">
                  <input value={candForm.first_name} onChange={(e) => setCandForm({ ...candForm, first_name: e.target.value })} placeholder="Prénom" data-testid="candidate-field-first_name" className="border border-white/10 bg-void px-3 py-2 text-sm text-white outline-none focus:border-cyan" />
                  <input value={candForm.last_name} onChange={(e) => setCandForm({ ...candForm, last_name: e.target.value })} placeholder="Nom" data-testid="candidate-field-last_name" className="border border-white/10 bg-void px-3 py-2 text-sm text-white outline-none focus:border-cyan" />
                </div>
                <input value={candForm.email} onChange={(e) => setCandForm({ ...candForm, email: e.target.value })} placeholder="E-mail" data-testid="candidate-field-email" className="w-full border border-white/10 bg-void px-3 py-2 text-sm text-white outline-none focus:border-cyan" />
                <input value={candForm.phone} onChange={(e) => setCandForm({ ...candForm, phone: e.target.value })} placeholder="Téléphone" data-testid="candidate-field-phone" className="w-full border border-white/10 bg-void px-3 py-2 text-sm text-white outline-none focus:border-cyan" />
                <select value={candForm.country} onChange={(e) => setCandForm({ ...candForm, country: e.target.value })} data-testid="candidate-field-country" className="w-full border border-white/10 bg-void px-3 py-2 text-sm text-white outline-none focus:border-cyan">
                  <option value="">Pays —</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setEditCand(false)} data-testid="candidate-cancel" className="flex-1 border border-white/15 py-2 text-xs font-600 text-ink-secondary hover:text-white">Annuler</button>
                  <button onClick={saveCandidate} disabled={savingCand} data-testid="candidate-save" className="flex-1 bg-cyan py-2 text-xs font-700 text-void hover:-translate-y-0.5 disabled:opacity-50">{savingCand ? "…" : "Enregistrer"}</button>
                </div>
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-surface p-5">
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_priority")}</div>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((pr) => (
                <button key={pr} onClick={() => setPriority(pr)} data-testid={`admin-priority-${pr}`}
                  className={`border px-3 py-1.5 text-xs font-600 uppercase tracking-wider transition-all hover:-translate-y-0.5 ${p.priority === pr ? PRIORITY_STYLE[pr] : "border-white/15 text-ink-secondary hover:text-white"}`}>
                  {priorityLabel(t, pr)}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-white/10 bg-surface p-5">
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_change_status")}</div>
            <select value={p.status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-select" className="w-full border border-white/10 bg-void px-4 py-3 text-sm text-white outline-none focus:border-cyan">
              {/* « Retenu / Non retenu par nos équipes » affichés en premier, à la demande, au-dessus de Brouillon */}
              {["RETENU PAR NOS ÉQUIPES", "NON RETENU PAR NOS ÉQUIPES", ...STATUSES.filter((s) => s !== "RETENU PAR NOS ÉQUIPES" && s !== "NON RETENU PAR NOS ÉQUIPES")].map((s) => <option key={s} value={s}>{statusLabel(t, s)}</option>)}
            </select>
            <button onClick={selectWinner} data-testid="admin-select-winner" className="mt-4 inline-flex w-full items-center justify-center gap-2 border border-cyan bg-cyan/10 px-4 py-3 text-sm font-700 uppercase tracking-wide text-cyan transition-all hover:bg-cyan hover:text-void">
              <Trophy size={16} /> {t("apd_select_project")}
            </button>
          </div>

          <div className="border border-white/10 bg-surface p-5">
            <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_favorites")}</div>
            <div className="flex flex-wrap gap-2">
              {FAVS.map((fv) => (
                <button key={fv} onClick={() => setFav(fv)} data-testid={`admin-fav-${fv}`}
                  className={`inline-flex items-center gap-1 border px-3 py-1.5 text-xs transition-all ${p.favorite === fv ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"}`}>
                  <Star size={12} className={p.favorite === fv ? "fill-void" : ""} /> {favLabel(fv)}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-white/10 bg-surface p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("apd_rating")}</span>
              <span className="font-display text-2xl font-900 text-cyan">{total}<span className="text-sm text-ink-tertiary">/80</span></span>
            </div>
            {analysis?.data?.notes && (
              <button onClick={autoFillRatings} data-testid="ratings-autofill"
                title="Positionne automatiquement les 8 curseurs d'après les notes de l'analyse IA — vous pouvez ensuite ajuster avant d'enregistrer"
                className="mb-4 inline-flex w-full items-center justify-center gap-2 border border-electric/50 bg-electric/10 px-3 py-2.5 text-[11px] font-700 uppercase tracking-wider text-electric transition-all hover:bg-electric hover:text-void">
                <Wand2 size={13} /> Pré-remplir depuis l'analyse IA
              </button>
            )}
            <div className="space-y-3">
              {CRITERIA.map((c) => (
                <div key={c}>
                  <div className="mb-1 flex justify-between text-xs"><span className="text-ink-secondary">{critLabel(c)}</span><span className="tabular-nums text-cyan">{ratings[c] || 0}/10</span></div>
                  <input type="range" min="0" max="10" value={ratings[c] || 0} onChange={(e) => setRatings({ ...ratings, [c]: Number(e.target.value) })} data-testid={`rating-${c}`} className="w-full accent-cyan" />
                </div>
              ))}
            </div>
            <button onClick={saveRatings} data-testid="admin-save-ratings" className="mt-5 w-full bg-elevated py-2.5 text-sm text-cyan transition-colors hover:bg-cyan hover:text-void">{t("apd_save_rating")}</button>
          </div>
        </div>
      </div>

      {/* Dossier de suivi de production (échanges, demandes, validations…) */}
      <div ref={dossierRef} data-testid="workspace-dossier">
        <ProjectDossier pid={pid} mode="admin" project={p} />
      </div>
    </div>
  );
}

const Block = ({ title, body, orig }) => (
  <div>
    <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{title}</div>
    <p className="text-base leading-relaxed text-white">{body || "—"}</p>
    {orig && <p className="mt-2 rounded-lg border border-white/10 bg-surface px-3 py-2 text-xs italic leading-relaxed text-ink-tertiary" data-testid="block-original">Original : {orig}</p>}
  </div>
);
const Meta = ({ l, v }) => (
  <div className="border border-white/10 bg-surface p-3">
    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-tertiary">{l}</div>
    <div className="mt-1 text-sm text-white">{v || "—"}</div>
  </div>
);
